import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

interface MouseGlowProps {
  enabled?: boolean;
  color?: string;
  size?: number;
  intensity?: number;
}

/**
 * Dynamic lighting effect that follows the mouse cursor
 * Creates a subtle glow on glass panels in Glassmorphism theme
 */
export const MouseGlow: React.FC<MouseGlowProps> = ({ 
  enabled = true,
  color = 'rgba(6, 182, 212, 0.15)',
  size = 400,
  intensity = 1
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animation for fluid movement
  const springConfig = { damping: 25, stiffness: 150, mass: 0.5 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, mouseX, mouseY, isVisible]);

  if (!enabled) return null;

  return (
    <motion.div
      className="pointer-events-none fixed inset-0 z-30 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="absolute rounded-full"
        style={{
          x: smoothX,
          y: smoothY,
          width: size,
          height: size,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
          opacity: intensity,
          filter: 'blur(40px)',
        }}
      />
    </motion.div>
  );
};

/**
 * Secondary ambient glow that adds depth
 */
export const AmbientGlow: React.FC<{ theme: string }> = ({ theme }) => {
  if (theme !== 'glassmorphism') return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {/* Top-left glow */}
      <div 
        className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full opacity-30 animate-glow-pulse"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
          filter: 'blur(100px)',
        }}
      />
      {/* Bottom-right glow */}
      <div 
        className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 rounded-full opacity-20 animate-glow-pulse"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
          filter: 'blur(100px)',
          animationDelay: '1s',
        }}
      />
    </div>
  );
};
